import { inject, DestroyRef, Service } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { tap } from 'rxjs/operators';
import { Api } from '@api/api';
import { findAllPages, findPage } from '@api/functions';
import type { PageRegistryDto } from '@api/models';
import { PageTypeEnum } from '@api/models';
import { parseUrlPath } from '../helpers/parse-url-path.function';

@Service()
export class PageRegistryService {
  private api = inject(Api);
  private destroyRef = inject(DestroyRef);
  private slugMap = new Map<string, PageRegistryDto>();

  registerPages(pages: PageRegistryDto[]): void {
    for (const page of pages) {
      this.slugMap.set(page.s, page);
    }
  }

  getPageType(slug: string): PageTypeEnum | undefined {
    return this.slugMap.get(slug)?.t;
  }

  getRedirectSlug(slug: string): string | undefined {
    return this.slugMap.get(slug)?.rS;
  }

  hasSlug(slug: string): boolean {
    return this.slugMap.has(slug);
  }

  isSlugInPath(path: string): boolean {
    const { slug } = parseUrlPath(path);
    return this.slugMap.has(slug);
  }

  fetchAndRegisterPage$(slug: string) {
    return this.api.invoke(findPage, { slug })
      .pipe(
        tap({ next: response => this.registerPages([response]) }),
      );
  }

  fetchAndRegisterAllPages(): void {
    this.api.invoke(findAllPages)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => this.registerPages(response.data),
      });
  }
}
